import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNotify } from "@/hooks/useNotify";
import AdvancedLogoUpload from "@/components/ui/advanced-logo-upload";
import { useImpersonationGuard } from "@/hooks/useImpersonationGuard";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Building, Mail, Phone, Globe, MapPin, CreditCard } from "lucide-react";

export default function CompanySettings() {
  const [isLoading, setIsLoading] = useState(false);
  const { user, profile } = useAuth();
  const notify = useNotify();
  const { guardWrite } = useImpersonationGuard();

  // Company data state
  const [companyName, setCompanyName] = useState("");
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [companyStreetAddress, setCompanyStreetAddress] = useState("");
  const [companySuburb, setCompanySuburb] = useState("");
  const [companyCity, setCompanyCity] = useState("");
  const [companyProvinceState, setCompanyProvinceState] = useState("");
  const [companyPostalCode, setCompanyPostalCode] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isVatRegistered, setIsVatRegistered] = useState(false);
  const [vatNumber, setVatNumber] = useState("");
  const [vatRate, setVatRate] = useState<number | ''>(15);

  // Banking information state
  const [useBankInfo, setUseBankInfo] = useState(false);
  const [bankName, setBankName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [branchCode, setBranchCode] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  const fetchCompanyData = async () => {
    if (!user?.id || !profile?.company_id) return;

    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select(`
          name,
          email,
          website,
          street_address,
          suburb,
          city,
          province_state,
          postal_code,
          company_phone_number,
          logo_url,
          is_vat_registered,
          vat_number,
          "VAT Rate",
          "Use Bank Info",
          "Bank Name",
          "Branch Name",
          "Branch Code",
          "Account Holder",
          "Account Number"
        `)
        .eq('id', profile.company_id)
        .single();

      if (companyError) {
        console.error('Company fetch error:', companyError);
        throw companyError;
      }

      if (companyData) {
        setCompanyName(companyData.name || "");
        setEmail(companyData.email || "");
        setWebsite(companyData.website || "");
        setCompanyStreetAddress(companyData.street_address || "");
        setCompanySuburb(companyData.suburb || "");
        setCompanyCity(companyData.city || "");
        setCompanyProvinceState(companyData.province_state || "");
        setCompanyPostalCode(companyData.postal_code || "");
        setCompanyPhoneNumber(companyData.company_phone_number || "");
        setLogoUrl(companyData.logo_url || null);
        setIsVatRegistered(companyData.is_vat_registered || false);
        setVatNumber(companyData.vat_number || "");
        setVatRate(companyData["VAT Rate"] || 15);
        
        // Banking information
        setUseBankInfo(companyData["Use Bank Info"] || false);
        setBankName(companyData["Bank Name"] || "");
        setBranchName(companyData["Branch Name"] || "");
        setBranchCode(companyData["Branch Code"] || "");
        setAccountHolder(companyData["Account Holder"] || "");
        setAccountNumber(companyData["Account Number"] || "");
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      notify.error("Failed to load company data");
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, [user?.id, profile?.company_id]);

  const handleSaveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guardWrite('edit company settings')) return;
    
    if (!user?.id || !profile?.company_id) {
      notify.error("You must be logged in to update company details.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const updateData = {
        name: companyName,
        email: email,
        website: website,
        street_address: companyStreetAddress,
        suburb: companySuburb,
        city: companyCity,
        province_state: companyProvinceState,
        postal_code: companyPostalCode,
        company_phone_number: companyPhoneNumber,
        logo_url: logoUrl,
        is_vat_registered: isVatRegistered,
        vat_number: isVatRegistered ? vatNumber : null,
        "VAT Rate": isVatRegistered ? (vatRate === '' ? 0 : vatRate) : 15,
        "Use Bank Info": useBankInfo,
        "Bank Name": useBankInfo ? bankName : null,
        "Branch Name": useBankInfo ? branchName : null,
        "Branch Code": useBankInfo ? branchCode : null,
        "Account Holder": useBankInfo ? accountHolder : null,
        "Account Number": useBankInfo ? accountNumber : null,
        updated_at: new Date().toISOString()
      };

      const { data: updateResult, error: companyError } = await supabase
        .from('companies')
        .update(updateData)
        .eq("id", profile.company_id)
        .select();
      
      if (companyError) {
        throw companyError;
      }
      
      if (!updateResult || updateResult.length === 0) {
        throw new Error(
          'Update was not saved. You may not have permission to edit this company.'
        );
      }
      
      notify.success("Your company information has been updated successfully.");

      // Re-fetch data to ensure UI reflects the changes
      await fetchCompanyData();
      
    } catch (error: any) {
      console.error('Company update failed:', error);
      notify.error(error.message || "An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Company Information
          </CardTitle>
          <CardDescription>
            Update your company details and branding
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveCompany} className="space-y-6">
            {/* Logo Upload */}
            <div className="flex justify-center mb-6">
              <AdvancedLogoUpload 
                onImageUpload={setLogoUrl}
                initialImage={logoUrl || undefined}
              />
            </div>

            {/* Basic Company Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="company@example.com"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="companyPhoneNumber">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="companyPhoneNumber"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    className="pl-10"
                    value={companyPhoneNumber}
                    onChange={(e) => setCompanyPhoneNumber(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="website"
                    type="text"
                    placeholder="www.example.com"
                    className="pl-10"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Address
              </h3>
              
              <div className="space-y-2">
                <Label htmlFor="companyStreetAddress">Street Address</Label>
                <Input
                  id="companyStreetAddress"
                  value={companyStreetAddress}
                  onChange={(e) => setCompanyStreetAddress(e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companySuburb">Suburb</Label>
                  <Input
                    id="companySuburb"
                    value={companySuburb}
                    onChange={(e) => setCompanySuburb(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyCity">City</Label>
                  <Input
                    id="companyCity"
                    value={companyCity}
                    onChange={(e) => setCompanyCity(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyProvinceState">Province/State</Label>
                  <Input
                    id="companyProvinceState"
                    value={companyProvinceState}
                    onChange={(e) => setCompanyProvinceState(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="companyPostalCode">Postal Code</Label>
                <Input
                  id="companyPostalCode"
                  value={companyPostalCode}
                  onChange={(e) => setCompanyPostalCode(e.target.value)}
                />
              </div>
            </div>

            {/* VAT Information */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isVatRegistered"
                  checked={isVatRegistered}
                  onCheckedChange={setIsVatRegistered}
                />
                <Label htmlFor="isVatRegistered">VAT Registered</Label>
              </div>
              
              {isVatRegistered && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vatNumber">VAT Number</Label>
                    <Input
                      id="vatNumber"
                      value={vatNumber}
                      onChange={(e) => setVatNumber(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vatRate">VAT Rate (%)</Label>
                    <Input
                      id="vatRate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={vatRate}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') setVatRate('');
                        else { const num = parseFloat(val); if (!isNaN(num)) setVatRate(num); }
                      }}
                      onBlur={() => { if (vatRate === '') setVatRate(0); }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Banking Information */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="useBankInfo"
                  checked={useBankInfo}
                  onCheckedChange={setUseBankInfo}
                />
                <Label htmlFor="useBankInfo" className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Include Banking Information
                </Label>
              </div>
              
              {useBankInfo && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bankName">Bank Name</Label>
                    <Input
                      id="bankName"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="branchName">Branch Name</Label>
                    <Input
                      id="branchName"
                      value={branchName}
                      onChange={(e) => setBranchName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="branchCode">Branch Code</Label>
                    <Input
                      id="branchCode"
                      value={branchCode}
                      onChange={(e) => setBranchCode(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="accountHolder">Account Holder</Label>
                    <Input
                      id="accountHolder"
                      value={accountHolder}
                      onChange={(e) => setAccountHolder(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="accountNumber">Account Number</Label>
                    <Input
                      id="accountNumber"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Company Settings"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}